import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import MetaDirectoCard from '@/components/MetaDirectoCard';

type KapsoConfig = {
  hasApiKey: boolean;
  hasPhoneNumberId: boolean;
  phoneNumberId?: string;
  connectionStatus?: string;
  hasTemplateFallback?: boolean;
  templateName?: string;
  templateLanguage?: string;
};

const KapsoSettings: React.FC = () => {
  const navigate = useNavigate();
  const [kapsoApiKey, setKapsoApiKey] = useState('');
  const [kapsoPhoneNumberId, setKapsoPhoneNumberId] = useState('');
  const [kapsoTemplateName, setKapsoTemplateName] = useState('');
  const [kapsoTemplateLanguage, setKapsoTemplateLanguage] = useState('es_AR');
  const [kapsoTestPhone, setKapsoTestPhone] = useState('');
  const [kapsoConfig, setKapsoConfig] = useState<KapsoConfig | null>(null);
  const [status, setStatus] = useState<'connected' | 'disconnected' | 'not_configured' | 'error' | 'unknown'>('unknown');
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const fetchStatus = async () => {
    try {
      const res = await axios.get('/api/whatsapp/status');
      setStatus((res.data?.status as any) || 'unknown');
    } catch {
      setStatus('unknown');
    }
  };

  const fetchDiagnostic = async () => {
    try {
      const res = await axios.get('/api/whatsapp/diagnostic');
      if (res.data?.diagnostic) {
        setKapsoConfig({
          hasApiKey: res.data.diagnostic.hasApiKey,
          hasPhoneNumberId: res.data.diagnostic.hasPhoneNumberId,
          phoneNumberId: res.data.diagnostic.phoneNumberId,
          connectionStatus: res.data.diagnostic.connectionStatus,
          hasTemplateFallback: !!res.data.diagnostic.hasTemplateFallback,
          templateName: res.data.diagnostic.templateName || '',
          templateLanguage: res.data.diagnostic.templateLanguage || 'es_AR'
        });
        setKapsoTemplateName(res.data.diagnostic.templateName || '');
        setKapsoTemplateLanguage(res.data.diagnostic.templateLanguage || 'es_AR');
        setStatus((res.data.diagnostic.connectionStatus as any) || 'unknown');
      }
    } catch (error) {
      console.error('Error obteniendo diagnóstico Kapso:', error);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchDiagnostic();
  }, []);

  const saveKapsoConfig = async () => {
    if (!kapsoApiKey && !kapsoPhoneNumberId && !kapsoTemplateName) {
      toast.error('Ingresa la API Key, el Phone Number ID o un template por defecto');
      return;
    }

    setLoading(true);
    setTestResult(null);
    try {
      const response = await axios.post('/api/whatsapp/configure', {
        apiKey: kapsoApiKey || undefined,
        phoneNumberId: kapsoPhoneNumberId || undefined,
        templateName: kapsoTemplateName || undefined,
        templateLanguage: kapsoTemplateLanguage || undefined
      });

      if (response.data.success) {
        toast.success('Configuración de WhatsApp guardada y verificada');
        fetchStatus();
        fetchDiagnostic();
      } else {
        toast.error(response.data.message || 'No se pudo verificar la conexión');
      }
    } catch (error: any) {
      console.error('Error guardando config Kapso:', error);
      toast.error(error?.response?.data?.error || 'Error guardando configuración');
    } finally {
      setLoading(false);
    }
  };

  const testKapsoConnection = async () => {
    if (!kapsoTestPhone.trim()) {
      toast.error('Ingresa un número de prueba');
      return;
    }

    setLoading(true);
    setTestResult(null);
    try {
      const response = await axios.post('/api/whatsapp/test', { phone: kapsoTestPhone.trim() });
      if (response.data.success) {
        setTestResult({ success: true, message: `Mensaje de prueba enviado a ${kapsoTestPhone}` });
        toast.success('Mensaje de prueba enviado');
      } else {
        setTestResult({ success: false, message: response.data.error || 'Error al enviar mensaje de prueba' });
        toast.error('Error al enviar mensaje de prueba');
      }
    } catch (error: any) {
      console.error('Error probando Kapso:', error);
      setTestResult({ success: false, message: error?.response?.data?.error || 'Error al enviar mensaje de prueba' });
      toast.error(error?.response?.data?.error || 'Error al enviar mensaje de prueba');
    } finally {
      setLoading(false);
    }
  };

  const statusLabel = (() => {
    if (status === 'connected') return { text: 'Conectado', variant: 'default' as const };
    if (status === 'not_configured') return { text: 'No configurado', variant: 'destructive' as const };
    if (status === 'error') return { text: 'Error', variant: 'destructive' as const };
    return { text: 'Desconectado', variant: 'secondary' as const };
  })();

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Configuración de WhatsApp (Kapso)</h1>
          <p className="text-sm text-muted-foreground">
            Usa Kapso WhatsApp Business API con coexistencia para seguir usando tu número actual en el celular y en el sistema.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate('/settings')}>Volver a Configuración</Button>
      </div>

      <div className="border rounded-lg bg-card p-6 space-y-6">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={kapsoConfig?.hasApiKey && kapsoConfig?.hasPhoneNumberId ? 'default' : 'destructive'}>
            {kapsoConfig?.hasApiKey && kapsoConfig?.hasPhoneNumberId ? 'Credenciales guardadas' : 'No configurado'}
          </Badge>
          <Badge variant={statusLabel.variant}>{statusLabel.text}</Badge>
          {kapsoConfig?.phoneNumberId && (
            <Badge variant="secondary">Phone ID: {kapsoConfig.phoneNumberId}</Badge>
          )}
          <Badge variant={kapsoConfig?.hasTemplateFallback ? 'default' : 'secondary'}>
            {kapsoConfig?.hasTemplateFallback ? `Template: ${kapsoConfig?.templateName}` : 'Sin template fallback'}
          </Badge>
        </div>

        <div className="p-4 bg-muted/40 rounded-md border border-border">
          <p className="text-sm text-muted-foreground">
            Para obtener tus credenciales, registrate en{' '}
            <a href="https://kapso.ai" target="_blank" rel="noopener noreferrer" className="underline font-medium text-blue-600">
              kapso.ai
            </a>
            . La coexistencia permite usar el mismo número en tu celular y en el sistema.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">API Key de Kapso *</Label>
              <Input
                type="password"
                value={kapsoApiKey}
                onChange={(e) => setKapsoApiKey(e.target.value)}
                placeholder={kapsoConfig?.hasApiKey ? '••••••••••••••••' : 'Ingresa tu API Key de Kapso'}
              />
              <p className="mt-1 text-xs text-muted-foreground">Obtené tu API Key desde el dashboard de Kapso.</p>
            </div>

            <div>
              <Label className="mb-2 block">Phone Number ID *</Label>
              <Input
                type="text"
                value={kapsoPhoneNumberId}
                onChange={(e) => setKapsoPhoneNumberId(e.target.value)}
                placeholder={kapsoConfig?.hasPhoneNumberId ? '(configurado)' : 'Ej: 647015955153740'}
              />
              <p className="mt-1 text-xs text-muted-foreground">ID del número de teléfono de WhatsApp Business.</p>
            </div>

            <div>
              <Label className="mb-2 block">Template por defecto (opcional)</Label>
              <Input
                type="text"
                value={kapsoTemplateName}
                onChange={(e) => setKapsoTemplateName(e.target.value)}
                placeholder="Ej: presupuesto_recordatorio"
              />
              <p className="mt-1 text-xs text-muted-foreground">Se usa para reabrir conversaci�n fuera de 24 horas.</p>
            </div>

            <div>
              <Label className="mb-2 block">Idioma template (opcional)</Label>
              <Input
                type="text"
                value={kapsoTemplateLanguage}
                onChange={(e) => setKapsoTemplateLanguage(e.target.value)}
                placeholder="es_AR"
              />
              <p className="mt-1 text-xs text-muted-foreground">Ejemplos: es_AR, es, en_US.</p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/settings')}>Cancelar</Button>
              <Button onClick={saveKapsoConfig} disabled={loading || (!kapsoApiKey && !kapsoPhoneNumberId && !kapsoTemplateName)}>
                {loading ? 'Guardando...' : 'Guardar configuración'}
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="mb-2 block">Probar conexión</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={kapsoTestPhone}
                  onChange={(e) => setKapsoTestPhone(e.target.value)}
                  placeholder="Número de prueba (ej: 5493416858698)"
                />
                <Button variant="secondary" onClick={testKapsoConnection} disabled={loading || !kapsoConfig?.hasPhoneNumberId}>
                  {loading ? 'Enviando...' : 'Enviar prueba'}
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Envía un mensaje de prueba para verificar la conexión.</p>
            </div>

            {testResult && (
              <div className={`p-4 rounded-md ${testResult.success ? 'bg-green-50 dark:bg-green-900/20 border border-green-200' : 'bg-red-50 dark:bg-red-900/20 border border-red-200'}`}>
                <p className={`text-sm ${testResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'}`}>
                  {testResult.success ? '✓' : '✗'} {testResult.message}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Envío directo por Meta con app y token propios (solo lo ve un administrador) */}
      <MetaDirectoCard />
    </div>
  );
};

export default KapsoSettings;

